import { useState } from 'react';

import Text from '../components/Text';
import { detecterPlateforme } from '../helpers/plateforme';
import { t } from '../i18n/langue';
import type { ClePlateforme } from '../helpers/plateforme';

import {
    ConsigneAndroidAutre,
    ConsigneAndroidChrome,
    ConsigneInApp,
    ConsigneIosSafari,
    ConsigneIosTiers,
    ConsigneOrdinateur,
} from './sas/consignes';

/**
 * Le sas — hors standalone, `m.` ne montre rien d'autre. #21, #79.
 *
 * ## Un mur assumé, et ce qui l'a rendu nécessaire
 *
 * **Aucun formulaire de connexion dans un onglet de navigateur.** Ce n'est pas de
 * la rigueur pour la rigueur : sur iPhone, le stockage d'une web app installée est
 * séparé de celui de Safari — « separate cookies and storage from the browser »
 * (Apple, WWDC23 session 10120, source primaire vérifiée par #35). Se connecter
 * ici obligerait donc à se reconnecter après l'installation, et produirait **deux
 * sessions par appareil**.
 *
 * Pire : la permission de notifier n'existe pas hors app installée (#13). Un `m.`
 * pleinement utilisable dans un onglet fabriquerait des utilisateurs convaincus
 * d'avoir l'app tout en étant **injoignables** — la panne silencieuse que toute la
 * carte #12 s'organise à éviter.
 *
 * La seule échappatoire est `app.`, jamais un `m.` dégradé.
 *
 * ## Six consignes, un défaut, et une porte de sortie
 *
 * L'UA choisit la consigne **par défaut** ; chaque écran porte un lien qui déplie
 * les six. C'est ce qui rend le reniflage acceptable ici alors que #14 l'a écarté
 * pour la redirection — voir `helpers/plateforme.ts`.
 */

/** Les chemins qui désignent une conversation précise (#20, #77). */
const CHEMIN_CONVERSATION = /^\/messaging\/[^/]+/;

/**
 * Le sas reconnaît le lien profond — décision #20.
 *
 * Quelqu'un qui clique sur une notification et atterrit dans un onglet (lien
 * recopié, comportement `implementation-defined` du champ `navigate`, app
 * désinstallée sans purge de l'abonnement) a déjà l'app. Lui expliquer comment
 * l'installer serait absurde.
 *
 * Le refus, lui, ne bouge pas : l'assouplir recréerait les deux sessions par
 * appareil. **Seul le texte change.** Aucune API ne permet de lancer une PWA
 * depuis un onglet ; le texte est littéralement tout ce qu'on peut faire.
 */
function porteUnLienProfond(): boolean {
    return CHEMIN_CONVERSATION.test(window.location.pathname);
}

export default function SasInstallation() {
    // Détecté une seule fois : l'UA ne change pas en cours de session, et le
    // recalculer à chaque rendu écraserait le choix manuel de l'utilisateur.
    const [consigne, setConsigne] = useState<ClePlateforme>(() => detecterPlateforme());
    const [lienProfond] = useState(porteUnLienProfond);

    const choisir = (cle: ClePlateforme) => setConsigne(cle);

    if (consigne === 'desktop') {
        return (
            <div className="tf-shell h-full overflow-y-auto bg-main-bg-color">
                <ConsigneOrdinateur />
            </div>
        );
    }

    const Consigne = {
        'ios-safari': ConsigneIosSafari,
        'ios-other': ConsigneIosTiers,
        inapp: ConsigneInApp,
        'android-chrome': ConsigneAndroidChrome,
        'android-other': ConsigneAndroidAutre,
    }[consigne];

    return (
        <div className="tf-shell flex h-full flex-col overflow-y-auto bg-white">
            {lienProfond && (
                <div className="bg-main-color-light px-6 py-4">
                    <Text variante="sousTitre" as="h2" ton="marque">
                        {t('sas.messageALire')}
                    </Text>
                    <Text variante="petit" className="mt-1 block leading-snug">
                        {t('sas.ouvrezDepuisAccueil')}
                    </Text>
                </div>
            )}

            <Consigne onChoisirConsigne={choisir} />
        </div>
    );
}
